import { FinancialAccount } from "@common/types/financialAccount";
import { CellValue } from "@components/spreadsheet/types";

export interface FinancialAccountClipboard {
    toClipboard: (value: CellValue) => string;
    fromClipboard: (text: string) => number | null;
}

const NUMERIC_ID_PATTERN = /^\d+$/;

const normalizeName = (name: string) => name.trim().toLowerCase();

export function createFinancialAccountClipboard(
    accounts: readonly FinancialAccount[],
): FinancialAccountClipboard {
    const accountsById = new Map(
        accounts.map((account) => [account.id, account]),
    );
    const idsByName = new Map<string, number[]>();
    accounts.forEach((account) => {
        if (!account.name) {
            return;
        }
        const name = normalizeName(account.name);
        idsByName.set(name, [...(idsByName.get(name) ?? []), account.id]);
    });

    return {
        toClipboard: (value) => {
            if (typeof value !== "number") {
                return "";
            }
            const account = accountsById.get(value);
            if (!account) {
                return "";
            }
            const normalizedName = account.name
                ? normalizeName(account.name)
                : "";
            const hasUniqueName =
                normalizedName !== "" &&
                idsByName.get(normalizedName)?.length === 1;
            const nameMatchesKnownAccountId =
                NUMERIC_ID_PATTERN.test(normalizedName) &&
                accountsById.has(Number(normalizedName));
            return hasUniqueName && account.name && !nameMatchesKnownAccountId
                ? account.name
                : String(account.id);
        },
        fromClipboard: (text) => {
            const trimmedText = text.trim();
            const accountId = Number(trimmedText);
            if (
                NUMERIC_ID_PATTERN.test(trimmedText) &&
                accountsById.has(accountId)
            ) {
                return accountId;
            }
            const matchingIds = idsByName.get(normalizeName(trimmedText));
            return matchingIds?.length === 1 ? matchingIds[0] : null;
        },
    };
}
